import React from 'react'
import { useIntl } from 'react-intl'
import { Box, Chip, List, ListItem, ListItemAvatar, ListItemText, Stack, Tooltip, Typography } from '@mui/material'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'

import Avatar, { AvatarSize } from '@app/src/components/Ui/Avatar'
import AvailabilityStatusDisplay from './AvailabilityStatusDisplay'
import type { BaseTemplate, LinkedTemplate } from './availabilityHelpers'
import { getSolutionTypeLabel } from './availabilityHelpers'

export type { BaseTemplate } from './availabilityHelpers'

export interface TemplateAvailabilityListProps<T extends BaseTemplate> {
  templates: T[]
  icon?: React.ReactNode
  avatarColor?: string
  linkedTemplatesLabel?: string
  linkedTemplatesIcon?: React.ReactNode
  renderAction?: (template: T) => React.ReactNode
  renderDetails?: (template: T) => React.ReactNode
}

function TemplateAvailabilityList<T extends BaseTemplate>({
  templates,
  icon = <FactCheckOutlinedIcon fontSize="large" />,
  avatarColor,
  linkedTemplatesLabel,
  linkedTemplatesIcon,
  renderAction,
  renderDetails,
}: TemplateAvailabilityListProps<T>) {
  const { formatMessage } = useIntl()

  const tooltipChipSx = {
    height: '20px',
    fontSize: '12px',
    backgroundColor: 'grey.200',
  }

  const renderAvailabilityTooltip = (linkedTemplate: LinkedTemplate): React.ReactNode => {
    const hasSolutionRestrictions = Boolean(
      linkedTemplate.solutionAvailability && linkedTemplate.solutionAvailability.length > 0,
    )
    const hasOrgRestrictions = Boolean(
      linkedTemplate.organizationAvailability && linkedTemplate.organizationAvailability.length > 0,
    )

    return (
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <CategoryOutlinedIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
            <Typography variant="caption" component="span" sx={{ whiteSpace: 'nowrap' }}>
              {hasSolutionRestrictions
                ? formatMessage({ id: 'configurations.templateConfig.availability.restrictedToSolutionTypes' })
                : formatMessage({ id: 'configurations.templateConfig.availability.availableForAllSolutionTypes' })}
            </Typography>
          </Box>
          {hasSolutionRestrictions && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              {(linkedTemplate.solutionAvailability ?? []).map(type => (
                <Chip
                  key={type}
                  label={getSolutionTypeLabel(type, formatMessage)}
                  size="small"
                  variant="filled"
                  sx={tooltipChipSx}
                />
              ))}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <BusinessOutlinedIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
            <Typography variant="caption" component="span" sx={{ whiteSpace: 'nowrap' }}>
              {hasOrgRestrictions
                ? formatMessage({ id: 'configurations.templateConfig.availability.restrictedToOrganizations' })
                : formatMessage({ id: 'configurations.templateConfig.availability.availableForAllOrganizations' })}
            </Typography>
          </Box>
          {hasOrgRestrictions && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              {(linkedTemplate.organizationAvailability ?? []).slice(0, 5).map(org => (
                <Chip
                  key={org.id}
                  label={org.organization?.name ?? `Org #${org.organizationId}`}
                  size="small"
                  variant="filled"
                  sx={tooltipChipSx}
                />
              ))}
              {(linkedTemplate.organizationAvailability ?? []).length > 5 && (
                <Typography variant="caption" component="span">
                  {formatMessage(
                    { id: 'general.andAdditional' },
                    { amount: (linkedTemplate.organizationAvailability ?? []).length - 5 },
                  )}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Stack>
    )
  }

  const hasAvailabilityData = (linkedTemplate: LinkedTemplate) =>
    linkedTemplate.solutionAvailability !== undefined || linkedTemplate.organizationAvailability !== undefined

  const renderLinkedTemplates = (template: T) => {
    if (!template.linkedTemplates || template.linkedTemplates.length === 0) {
      return null
    }

    // Sort linked templates alphabetically by name
    const sortedLinkedTemplates = [...template.linkedTemplates].sort((a, b) => a.name.localeCompare(b.name))

    const linkedIcon = linkedTemplatesIcon ?? <ArticleOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />

    return (
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5} mt={2}>
        {linkedIcon}
        <Typography variant="caption" color="text.secondary">
          {linkedTemplatesLabel}:
        </Typography>
        {sortedLinkedTemplates.map(linkedTemplate =>
          hasAvailabilityData(linkedTemplate) ? (
            <Tooltip
              key={linkedTemplate.id}
              title={renderAvailabilityTooltip(linkedTemplate)}
              arrow
              placement="top"
              slotProps={{
                popper: {
                  sx: {
                    '& .MuiTooltip-tooltip': {
                      maxWidth: '500px',
                    },
                  },
                },
              }}
            >
              <Chip label={linkedTemplate.name} size="small" variant="outlined" sx={{ cursor: 'help' }} />
            </Tooltip>
          ) : (
            <Chip key={linkedTemplate.id} label={linkedTemplate.name} size="small" variant="outlined" />
          ),
        )}
      </Stack>
    )
  }

  const renderTemplateItem = (template: T, index: number) => (
    <ListItem
      key={template.id ?? index}
      sx={({ palette }) => ({
        bgcolor: 'background.paper',
        borderBottom: `1px solid ${palette.grey[300]}`,
        '&:last-child': { borderBottom: 'none' },
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        gap: 2,
        py: 3,
        px: 3,
      })}
    >
      <Stack direction="row" flex="1 1 300px" minWidth={0}>
        <ListItemAvatar>
          <Avatar
            size={AvatarSize.XXL}
            sx={({ palette }) => ({
              backgroundColor: avatarColor || palette.secondary.main,
              border: `1px solid ${avatarColor || palette.secondary.main}`,
              color: palette.common.white,
              mr: 2,
            })}
          >
            {icon}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={template.name}
          primaryTypographyProps={{ fontWeight: 600, noWrap: true, fontSize: '16px' }}
          secondary={
            <Box component="span">
              <AvailabilityStatusDisplay
                solutionAvailability={template.solutionAvailability}
                organizationAvailability={template.organizationAvailability}
              />
              {renderDetails?.(template)}
              {renderLinkedTemplates(template)}
            </Box>
          }
          sx={{ minWidth: 0, p: 0, m: 0 }}
        />
      </Stack>
      {renderAction && (
        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          {renderAction(template)}
        </Stack>
      )}
    </ListItem>
  )

  return <List sx={{ bgcolor: 'background.paper', mt: 4 }}>{templates.map(renderTemplateItem)}</List>
}

export default TemplateAvailabilityList
